// Dynamically generated SystemMailOverview based on backend categories
// Statische Bereiche: signature, variable, admin categories, admin dictionary

import React, { useState } from 'react';
import {
  ManageAccounts,
  AccountBalance,
  Person,
  Notifications,
  Alarm,
  DesignServices,
  ImportContacts,
  AccountTree,
  Error,
  Help,
  Mail,
  Group,
} from '@mui/icons-material';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { SystemMailDialog } from './SystemMailDialog';
import { SystemMailCategoryOverview } from './Childs/SystemMailCategoryOverview';
import { useGetDictionaries } from '../hooks/reactQuerys/DictionaryQueries';
import { useGetDictionaryEntries } from '../hooks/reactQuerys/DictionaryEntryQueries';
import {
  useGetSystemMailCategories,
  useGetSystemMailHasDictionary,
  useGetSystemMails,
} from '../hooks/reactQuerys/SystemMailQueries';
import { ISystemMail } from '../Interfaces/ISystemMail';
import { useGetGlobalVariables } from '../hooks/reactQuerys/GlobalVariableQueries';
import { GlobalVariableOverview } from './Childs/GlobalVariableOverview';
import { EmailSignatureEdit } from '../Admin/Settings/EmailSignatureEdit';

interface IProps {
  isAdmin: boolean;
}

export const SystemMailOverview: React.FC<IProps> = ({ isAdmin }) => {
  const { currentView, idSystemMail } = useParams();
  const navigate = useNavigate();
  const base_url = isAdmin ? '/admin/system_mail' : '/system_mail';

  const { systemMails, isLoadingSystemMails, refetchSystemMails } =
    useGetSystemMails();
  const { systemMailCategories, isLoadingSystemMailCategories } =
    useGetSystemMailCategories();
  const {
    systemMailHasDictionary,
    isLoadingSystemMailHasDictionary,
    refetchSystemMailHasDictionary,
  } = useGetSystemMailHasDictionary();
  const { dictionaries, isLoadingDictionaries, refetchDictionaries } =
    useGetDictionaries();
  const {
    dictionaryEntries,
    isLoadingDictionaryEntries,
    refetchDictionaryEntries,
  } = useGetDictionaryEntries();
  const { globalVariables, isLoadingGlobalVariables, refetechGlobalVariables } =
    useGetGlobalVariables();

  const [helpObject, setHelpObject] = useState<ISystemMail | null>(null);

  // Icon Map
  const ICON_MAP: Record<string, React.ReactNode> = {
    ManageAccounts: <ManageAccounts />,
    AccountBalance: <AccountBalance />,
    AccountTree: <AccountTree />,
    Person: <Person />,
    Notifications: <Notifications />,
    Alarm: <Alarm />,
    DesignServices: <DesignServices />,
  };

  type VIEW = {
    key: string;
    id: number;
    title: string;
    icon: React.ReactNode;
    divider?: boolean;
  };

  // Dynamische Views aus dem Backend
  const dynamicViews: VIEW[] = systemMailCategories
    ? systemMailCategories.map(cat => ({
        key: cat.URL,
        id: cat.idSystemMailCategory,
        title: cat.SystemMailCategory,
        icon: ICON_MAP[cat.ICON] || <Error />,
        divider: false,
      }))
    : [];

  // Statische Ergänzungen
  const staticViews: VIEW[] = [
    {
      key: 'signature',
      id: 0,
      title: 'Signatur',
      icon: <DesignServices />,
      divider: true,
    },
    {
      key: 'variable',
      id: 0,
      title: 'Globale Variablen',
      icon: <ImportContacts />,
    },
  ];

  // Admin-spezifische Views
  // DKM: Wir derzeitig nicht benötigt, erfolgt direkt über die E-Mails
  const adminViews: VIEW[] =
    isAdmin && false
      ? [
          {
            key: 'categories',
            id: 0,
            title: 'Admin: Kategorien',
            icon: <ImportContacts />,
            divider: true,
          },
          {
            key: 'dictionary',
            id: 0,
            title: 'Admin: Wörterbücher',
            icon: <ImportContacts />,
          },
        ]
      : [];

  // Gesamte Navigation
  const allViews = [
    {
      key: 'all',
      id: -1,
      title: 'Alle Kategorien',
      icon: <Mail />,
      divider: false,
    },
    ...dynamicViews,
    ...staticViews,
    ...adminViews,
  ];

  const activeView = allViews.find(v => v.key === currentView);

  const onClose = () => navigate(`${base_url}/${currentView}`);
  const onSave = () => {
    refetchSystemMails();
    onClose();
  };

  if (
    isLoadingSystemMails ||
    isLoadingSystemMailCategories ||
    isLoadingSystemMailHasDictionary ||
    isLoadingDictionaries ||
    isLoadingDictionaryEntries ||
    isLoadingGlobalVariables
  ) {
    return <LoadingSpinner />;
  }
  if (
    !systemMails ||
    !systemMailCategories ||
    !systemMailHasDictionary ||
    !dictionaries ||
    !dictionaryEntries ||
    !globalVariables
  ) {
    return <Alert severity='error'>Fehler!</Alert>;
  }

  if (!activeView) {
    return <Alert severity='warning'>404 - Keine View gefunden.</Alert>;
  }

  return (
    <>
      {helpObject && (
        <Dialog
          open
          maxWidth='sm'
          fullWidth
          onClose={() => setHelpObject(null)}
        >
          <DialogContent>
            <Alert severity='info'>{helpObject.InfoText}</Alert>
          </DialogContent>
          <DialogActions>
            <Button variant='contained' onClick={() => setHelpObject(null)}>
              Okay
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {idSystemMail && (
        <SystemMailDialog
          idSystemMail={Number(idSystemMail)}
          systemMails={systemMails}
          systemMailCategories={systemMailCategories}
          systemMailHasDictionaryArray={systemMailHasDictionary}
          onSave={onSave}
          onClose={onClose}
          isAdmin={isAdmin}
          dictionaries={dictionaries}
          dictionaryEntries={dictionaryEntries}
          refetchDictionary={refetchDictionaries}
          refetchDictionaryEntry={refetchDictionaryEntries}
          refetchSystemMailHasDictionary={refetchSystemMailHasDictionary}
        />
      )}

      <Grid container spacing={2}>
        {/* Sidebar */}
        <Grid item xs={2}>
          <List>
            {allViews.map(v => (
              <React.Fragment key={v.key}>
                {v.divider && <Divider />}
                <ListItemButton
                  selected={currentView === v.key}
                  onClick={() => navigate(`${base_url}/${v.key}`)}
                >
                  <ListItemIcon>{v.icon}</ListItemIcon>
                  <ListItemText primary={v.title} />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </Grid>

        {/* Content */}
        <Grid item xs={10}>
          {activeView.id !== 0 ? (
            <>
              <Typography variant='h4'>
                Systemnachrichten: {activeView.title}
              </Typography>
              <Typography variant='h5' sx={{ mt: 1, mb: 2 }}>
                Empfängergruppe:
                {}
                {activeView.id === -1 ? (
                  <>
                    <Group sx={{ ml: 5 }} /> Mandanten, Notare und Mitarbeiter
                  </>
                ) : systemMailCategories.find(
                    m => m.idSystemMailCategory === activeView.id
                  )?.isInternal ? (
                  <>
                    <ManageAccounts sx={{ ml: 5 }} /> Notar/Mitarbeiter
                  </>
                ) : (
                  <>
                    <Person sx={{ ml: 5 }} /> Mandant
                  </>
                )}
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50}>Nr.</TableCell>
                    <TableCell width={50}>Info</TableCell>
                    <TableCell>Bezeichnung</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {systemMails
                    .filter(m => !m.isHidden)
                    .filter(
                      m =>
                        activeView.id === -1 ||
                        m.idSystemMailCategory === activeView.id
                    )
                    .map(m => (
                      <TableRow
                        key={m.idSystemMail}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { background: '#f5f5f5' },
                        }}
                        onClick={() =>
                          navigate(
                            `${base_url}/${currentView}/${m.idSystemMail}`
                          )
                        }
                      >
                        <TableCell>{m.idSystemMail}</TableCell>
                        <TableCell>
                          {m.InfoText !== null ? (
                            <IconButton
                              size='small'
                              onClick={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                setHelpObject(m);
                              }}
                              sx={{ height: 20, width: 20 }}
                            >
                              <Help />
                            </IconButton>
                          ) : (
                            <></>
                          )}
                        </TableCell>
                        <TableCell>{m.Title}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </>
          ) : isAdmin && currentView === 'categories' ? (
            <SystemMailCategoryOverview
              systemMailCategories={systemMailCategories}
              systemMailHasDictionary={systemMailHasDictionary}
              dictionaries={dictionaries}
              dictionaryEntries={dictionaryEntries}
            />
          ) : currentView === 'variable' ? (
            <GlobalVariableOverview
              globalVariables={globalVariables}
              refetechGlobalVariables={refetechGlobalVariables}
            />
          ) : currentView === 'signature' ? (
            <EmailSignatureEdit />
          ) : (
            <Alert severity='info'>404 - Bereich wurde nicht gefunden</Alert>
          )}
        </Grid>
      </Grid>
    </>
  );
};
